import { Dispatch, SetStateAction } from 'react';
import { useLingui } from '@lingui/react/macro';
import { endOfDay, startOfDay, subDays } from 'date-fns';

import { ArticleServiceChoice } from '@/api';
import {
  ARTICLE_DISPLAYED_STEPS,
  ARTICLE_SERVICE_TYPES,
  ARTICLE_TASK_TYPES,
  ARTICLE_WARRANTY_TYPES,
  ArticleServiceType,
} from '@/models/article';
import { useOrganizationWorkflows } from '@/models/organization';
import { REQUESTOR_TYPES } from '@/models/request';
import {
  getDisplayedSteps,
  getServiceTypes,
  hasContactClientTask,
  hasExchangeStep,
  hasStartExchangeEnabled,
  hasStartRepairEnabled,
  hasTransitNotClientPickupStep,
} from '@/models/workflow';
import { useCurrentOrganization, useCurrentSession } from '@/services/auth';

import { DEFAULT_TAB_SORT, type Tab } from './FiltersState';

export type SortConfig = {
  'to-do': {
    field: 'reference' | 'statusDueAt' | 'dueAt' | 'referencedAt';
    order: 'asc' | 'desc';
  };
  waiting: {
    field: 'reference' | 'statusDueAt' | 'dueAt' | 'referencedAt';
    order: 'asc' | 'desc';
  };
  all: {
    field: 'reference' | 'statusDueAt' | 'dueAt' | 'referencedAt';
    order: 'asc' | 'desc';
  };
};

export const useApplicantOptions = () => {
  const [organization] = useCurrentOrganization();
  const { data: workflows, isLoading: isWorkflowsLoading } = useOrganizationWorkflows(
    organization?.slug
  );

  if (isWorkflowsLoading) {
    return [];
  }

  const applicantOptions = REQUESTOR_TYPES.filter((requestorType) => {
    const organizationWorkflowsHaveApplicant = workflows?.length
      ? workflows.some((workflow) => workflow.type.replaceAll('_', '-') === requestorType.id)
      : true;

    return organizationWorkflowsHaveApplicant;
  });

  return applicantOptions;
};

export const useStepOptions = () => {
  const [organization] = useCurrentOrganization();
  const { data: workflows, isLoading: isWorkflowsLoading } = useOrganizationWorkflows(
    organization?.slug
  );

  if (isWorkflowsLoading) {
    return [];
  }

  const stepOptions = ARTICLE_DISPLAYED_STEPS.filter((displayedStep) => {
    if (['in-recycling', 'archived', 'completed'].includes(displayedStep.id)) {
      return true;
    }

    const organizationWorkflowsHaveStep = workflows?.length
      ? workflows.some((workflow) => getDisplayedSteps(workflow).includes(displayedStep.id))
      : true;

    return organizationWorkflowsHaveStep;
  });

  return stepOptions;
};

export const useTaskOptions = () => {
  const { i18n } = useLingui();
  const { currentSession } = useCurrentSession();
  const [organization] = useCurrentOrganization();
  const { data: workflows, isLoading: isWorkflowsLoading } = useOrganizationWorkflows(
    organization?.slug
  );

  if (isWorkflowsLoading) {
    return [];
  }

  const roles = organization
    ? currentSession?.roles.filter((role) => role.organization?.id === organization.id)
    : currentSession?.roles;

  const availableTasks = ARTICLE_TASK_TYPES.filter(
    (task) =>
      !!task.permissions.find((taskPermission) =>
        roles?.some((role) => role.role.permissions.includes(taskPermission))
      )
  )
    .filter((task) => {
      if (!workflows?.length) {
        return true;
      }

      switch (task.id) {
        case 'contact_client':
          return workflows.some((workflow) => hasContactClientTask(workflow));
        case 'create_shipment':
        case 'validate_transit':
        case 'prepare_shipment':
        case 'dropoff_shipment':
        case 'finalise_shipment':
        case 'verify_shipment_reception':
        case 'handle_shipment_issue':
          return workflows.some((workflow) => hasTransitNotClientPickupStep(workflow));
        case 'start_article_exchange':
          return workflows.some((workflow) => hasStartExchangeEnabled(workflow));
        case 'start_article_repair':
          return workflows.some((workflow) => hasStartRepairEnabled(workflow));
        case 'exchange_article':
          return workflows.some((workflow) => hasExchangeStep(workflow));
        default:
          return true;
      }
    })
    .sort((a, b) => i18n._(a.label).localeCompare(i18n._(b.label), i18n.locale));

  return availableTasks;
};

export const useServiceTypeOptions = () => {
  const [organization] = useCurrentOrganization();
  const { data: workflows, isLoading: isWorkflowsLoading } = useOrganizationWorkflows(
    organization?.slug
  );

  if (isWorkflowsLoading) {
    return [];
  }

  const serviceTypeOptions = ARTICLE_SERVICE_TYPES.filter((serviceType) => {
    const organizationWorkflowsHaveServiceType = workflows?.length
      ? workflows.some((workflow) => getServiceTypes(workflow).includes(serviceType.id))
      : true;

    return organizationWorkflowsHaveServiceType;
  });

  return serviceTypeOptions;
};

export const formatServiceTypeFilter = (
  serviceTypes: ArticleServiceType[]
): ArticleServiceChoice[] => {
  const serviceChoiceFilter: ArticleServiceChoice[] = [...serviceTypes];

  if (serviceTypes.includes('exchange')) {
    serviceChoiceFilter.push('exchange-archival');
  }

  return serviceChoiceFilter;
};

export const useWarrantyTypeOptions = () => {
  const [organization] = useCurrentOrganization();

  const warrantyTypeOptions = ARTICLE_WARRANTY_TYPES.filter((warrantyType) => {
    const organizationUsesDistributorWarranty =
      !!organization?.config.warranty?.distributorExtensionDurationInMonths;

    if (organizationUsesDistributorWarranty || !organization) {
      return [
        'applicable-without-extension',
        'applicable-with-extension',
        'not-applicable',
      ].includes(warrantyType.id);
    } else {
      return ['applicable', 'not-applicable'].includes(warrantyType.id);
    }
  });

  return warrantyTypeOptions;
};

export const useShowWarrantyTypeFilter = () => {
  const [organization] = useCurrentOrganization();
  const { data: workflows, isLoading: isWorkflowsLoading } = useOrganizationWorkflows(
    organization?.slug
  );

  return organization
    ? !isWorkflowsLoading &&
        !!workflows?.length &&
        workflows.some((workflow) => !!workflow.config.fields?.request?.articles?.warranty?.enabled)
    : true;
};

export const useDateOptions = () => {
  const { t } = useLingui();

  return [
    {
      label: t({
        id: 'requests.filters.createdAt.all',
        message: 'All',
      }),
      value: {
        start: null,
        end: null,
      },
      id: 'all',
    },
    {
      label: t({
        id: 'requests.filters.createdAt.last-7-days',
        message: 'Last 7 days',
      }),
      value: {
        start: startOfDay(subDays(new Date(), 6)).toISOString(),
        end: endOfDay(new Date()).toISOString(),
      },
      id: 'last-7-days',
    },
    {
      label: t({
        id: 'requests.filters.createdAt.last-30-days',
        message: 'Last 30 days',
      }),
      value: {
        start: startOfDay(subDays(new Date(), 29)).toISOString(),
        end: endOfDay(new Date()).toISOString(),
      },
      id: 'last-30-days',
    },
    {
      id: 'custom',
      label: t({
        id: 'requests.filters.createdAt.customLabel',
        message: 'Custom',
      }),
      value: {
        start: null,
        end: null,
      },
    },
  ];
};

export const handleChangeSort = (
  tab: Tab,
  columnValue: 'reference' | 'statusDueAt' | 'dueAt' | 'referencedAt',
  sort: {
    sort: SortConfig;
    setSort: Dispatch<SetStateAction<SortConfig>>;
  }
) => {
  sort?.setSort((prev) => {
    const prevTabSort = prev[tab];
    const newTabSort = {
      field:
        prevTabSort.field !== columnValue
          ? columnValue
          : prevTabSort.order === 'asc'
            ? columnValue
            : DEFAULT_TAB_SORT[tab].field,
      order: prevTabSort.field !== columnValue || prevTabSort.order === 'desc' ? 'asc' : 'desc',
    };

    return { ...prev, [tab]: newTabSort };
  });
};

export type SubLocationFilterOption = {
  id: string;
  label: string;
  atEntityId: string;
  subLocation: string;
};

export const useSubLocationOptions = (): SubLocationFilterOption[] => {
  const { currentSession } = useCurrentSession();

  const options =
    currentSession?.roles.reduce((acc, curr) => {
      const entity = curr.entity;
      const entitySubLocationOptions = entity?.placeConfig?.subLocationOptions;

      if (!entity || !entitySubLocationOptions?.length) {
        return acc;
      }

      if (acc.find((option) => option.atEntityId === entity.id)) {
        return acc;
      }

      acc.push(
        ...entitySubLocationOptions.map((subLocation) => ({
          id: `${entity.id}_${subLocation}`,
          label: `${entity.name} - ${subLocation}`,
          atEntityId: entity.id,
          subLocation,
        }))
      );

      return acc;
    }, [] as SubLocationFilterOption[]) ?? [];

  return options.toSorted((a, b) => a.label.localeCompare(b.label));
};

type SortOption = {
  id: 'reference' | 'statusDueAt' | 'dueAt' | 'referencedAt';
  label: string;
  ascLabel: string;
  descLabel: string;
};

export const useSortByOptions = (): SortOption[] => {
  const { t } = useLingui();

  return [
    {
      id: 'reference',
      label: t({ id: 'requests.table.column.reference.title', message: 'Reference' }),
      ascLabel: 'A-Z',
      descLabel: 'Z-A',
    },
    {
      id: 'statusDueAt',
      label: t({ id: 'requests.table.column.step-due-date.title', message: 'Step due date' }),
      ascLabel: t({ id: 'requests.table.column.step-due-date.asc', message: 'Newest → Oldest' }),
      descLabel: t({ id: 'requests.table.column.step-due-date.desc', message: 'Oldest → Newest' }),
    },
    {
      id: 'dueAt',
      label: t({ id: 'requests.table.column.request-due-date.title', message: 'Request due date' }),
      ascLabel: t({ id: 'requests.table.column.sorting.asc', message: 'Soonest first' }),
      descLabel: t({ id: 'requests.table.column.sorting.desc', message: 'Latest first' }),
    },
    {
      id: 'referencedAt',
      label: t({
        id: 'requests.table.column.request-creation-date.title',
        message: 'Creation date',
      }),
      ascLabel: t({ id: 'requests.table.column.sorting.asc', message: 'Soonest first' }),
      descLabel: t({ id: 'requests.table.column.sorting.desc', message: 'Latest first' }),
    },
  ];
};
